import { Plus } from 'lucide-react';
import React from 'react';
import { useNavigate, useParams } from 'react-router';
import { Link } from 'react-router-dom';
import {
    ChallengePageEntrySupportDto,
    ChallengeStatus
} from '@api/ChallengeClient';
import { Badge, BadgeType } from '@components/badge';
import { SortingRule, TableComponent } from '@components/table';
import { Button } from '@components/ui/button';
import { getCdnImage } from '@components/utils/cdn-image';
import { I18n } from '@core/i18n';
import { links } from '@core/router';
import { injectTSDI } from '@core/tsdi';
import { ChallengeDeleteModal } from '@src/communities/community/challenges/modals/challenge-delete-modal';
import { useStore } from './store';

export function getBadgeType(status?: ChallengeStatus): BadgeType {
    if (!status) {
        return 'grey';
    }
    switch (status) {
        case ChallengeStatus.CANCELED:
            return 'red';
        case ChallengeStatus.DRAFT:
            return 'grey';
        case ChallengeStatus.FINISHED:
            return 'green';
        case ChallengeStatus.IN_PROGRESS:
        case ChallengeStatus.SCHEDULED:
        case ChallengeStatus.PUBLISHED:
            return 'blue';
    }
}

export const Challenges = () => {
    const { communityId } = useParams();
    const { challenges, sorting, setSorting, dispatch, searchParams } =
        useStore(Number(communityId));

    const deleteDialogOpenState = React.useState(false);
    const [__, setDeleteDialogOpen] = deleteDialogOpenState;
    const [selectedChallenge, setSelectedChallenge] =
        React.useState<ChallengePageEntrySupportDto | null>(null);

    const { formatLongDate } = injectTSDI(I18n);

    const onSortingChange = (sorting?: SortingRule) => {
        if (sorting) {
            setSorting(sorting);
        }
    };
    const navigate = useNavigate();
    return (
        <>
            <div className="mb-4 flex justify-end">
                <Button
                    onClick={() =>
                        navigate(
                            links.communityChallengeCreateForm(
                                communityId.toString()
                            )
                        )
                    }
                >
                    <Plus />
                    Create Challenge
                </Button>
            </div>
            <ChallengeDeleteModal
                openState={deleteDialogOpenState}
                challengeId={selectedChallenge?.id}
                challengeName={selectedChallenge?.presentation.name.en}
                onSuccess={() => {
                    setDeleteDialogOpen(false);
                    setSelectedChallenge(null);
                    challenges.request([
                        {
                            ...searchParams,
                            sort: '-ruleConfig.dateTimeRange.from'
                        }
                    ]);
                }}
            />
            <TableComponent<ChallengePageEntrySupportDto[]>
                items={challenges}
                tableLayout="fixed"
                columns={[
                    {
                        Header: '',
                        accessor: 'presentation',
                        style: { width: 40 },
                        disableSortBy: true,
                        Cell: ({
                            row: {
                                original: { presentation }
                            }
                        }) => (
                            <>
                                {presentation?.photo && (
                                    <img
                                        className="size-10 rounded-md object-cover"
                                        src={getCdnImage(
                                            presentation.photo.bucketName,
                                            presentation.photo.objectKey,
                                            {
                                                resize: {
                                                    width: 50,
                                                    height: 50
                                                }
                                            }
                                        )}
                                    />
                                )}
                            </>
                        )
                    },
                    {
                        Header: 'Name',
                        id: 'name',
                        style: { width: 100 },
                        accessor: (challenge) => (
                            <div>
                                <Link
                                    to={links.communityChallengeEditForm(
                                        communityId.toString(),
                                        challenge.id.toString()
                                    )}
                                    data-testid={'challenge-details'}
                                >
                                    {challenge.presentation.name.en}
                                </Link>
                                <p className="text-sm text-muted-foreground">
                                    {challenge.dateTimeRange &&
                                        formatLongDate(
                                            challenge.dateTimeRange.from
                                        )}
                                </p>
                            </div>
                        ),
                        disableSortBy: true
                    },
                    {
                        Header: 'Status',
                        id: 'status',
                        style: { width: 150 },
                        accessor: (challenge) => (
                            <Badge type={getBadgeType(challenge?.status)}>
                                {challenge?.status
                                    ? challenge.status.toUpperCase()
                                    : ''}
                            </Badge>
                        ),
                        disableSortBy: true
                    }
                ]}
                contextMenu={[
                    {
                        label: 'Leaderboard',
                        hidden: (challenge) => !challenge.leaderboardEditable,
                        action: (challenge) => {
                            navigate(
                                links.communityChallengeLeaderboard(
                                    communityId.toString(),
                                    challenge.id.toString()
                                )
                            );
                        }
                    },
                    {
                        label: 'Edit',
                        action: (challenge) => {
                            navigate(
                                links.communityChallengeEditForm(
                                    communityId.toString(),
                                    challenge.id.toString()
                                )
                            );
                        }
                    },
                    {
                        label: 'Delete',
                        action: (challenge) => {
                            setSelectedChallenge(challenge);
                            setDeleteDialogOpen(true);
                        }
                    }
                ]}
                onPageSizeChange={(size) => dispatch({ type: 'size', size })}
                onChangePage={(page) => void dispatch({ type: 'page', page })}
                onSortingChange={onSortingChange}
                sorting={Array.isArray(sorting) ? sorting[0] : sorting}
            />
        </>
    );
};

export default Challenges;
